import { useRef, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { useDismiss } from "@/hooks/useDismiss";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { TextSizeControl } from "@/components/ui/TextSizeControl";
import { LanguageDropdown } from "@/components/nav/LanguageDropdown";
import { MimirRune } from "@/components/ui/MimirRune";
import { SkipLink } from "@/components/ui/SkipLink";
import { cn } from "@/utils/cn";
import { SharedProps } from "@/types";
import { SHELL_WIDTH } from '@/components/layout/PageContainer'

export function AppNav() {
  const page = usePage<SharedProps>();
  const { auth } = page.props;
  const url = page.url;
  const isActive = (href: string) => url === href || url.startsWith(`${href}/`);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  };

  useDismiss(navRef, closeMenu, menuOpen);

  function handleSignOut() {
    router.delete("/users/sign_out");
  }

  const navLink =
    "font-mono text-xs uppercase tracking-widest transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded py-2 px-1";

  return (
    <>
      <SkipLink />
      <nav
        ref={navRef}
        className={cn(
          'relative w-full mx-auto px-4 sm:px-6 lg:px-8 py-5 border-b border-line-soft',
          SHELL_WIDTH,
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Link
              href="/dashboard"
              aria-label="Mímír — dashboard"
              className="flex items-center gap-3 min-h-11 font-display text-base uppercase tracking-[0.34em] text-heading hover:text-em transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
            >
              <MimirRune />
              <span className="hidden sm:inline">Mímír</span>
            </Link>
            {auth.user && (
              <div className="hidden sm:flex items-center gap-6">
                <Link
                  href="/vocabulary"
                  aria-current={isActive("/vocabulary") ? "page" : undefined}
                  className={cn(
                    navLink,
                    "relative",
                    isActive("/vocabulary")
                      ? "text-accent after:absolute after:inset-x-1 after:-bottom-[21px] after:h-px after:bg-accent"
                      : "text-nav-link hover:text-em",
                  )}
                >
                  Vocabulary
                </Link>
                {auth.user.role === "admin" && (
                  <Link
                    href="/admin"
                    aria-current={isActive("/admin") ? "page" : undefined}
                    className={cn(
                      navLink,
                      isActive("/admin") ? "text-accent" : "text-nav-link hover:text-em",
                    )}
                  >
                    Admin
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {auth.user && <LanguageDropdown />}
            <TextSizeControl className="hidden sm:block" />
            <ThemeToggle />
            {auth.user && (
              <>
                <span
                  className="hidden sm:block font-mono text-xs tracking-wide text-muted truncate max-w-[11rem]"
                  title={auth.user.email}
                >
                  {auth.user.email}
                </span>
                <button
                  onClick={handleSignOut}
                  className="hidden sm:inline-flex items-center font-mono text-[11px] uppercase tracking-widest text-muted hover:text-em transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded py-2 px-1"
                >
                  Sign out
                </button>
                <button
                  ref={triggerRef}
                  type="button"
                  onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
                  aria-label="Menu"
                  aria-expanded={menuOpen}
                  aria-controls="app-nav-menu"
                  className="sm:hidden flex items-center justify-center w-11 h-11 rounded-md text-nav-toggle hover:text-nav-toggle-hover transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    {menuOpen ? (
                      <path d="M6 6l12 12M18 6L6 18" />
                    ) : (
                      <path d="M3 6h18M3 12h18M3 18h18" />
                    )}
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {menuOpen && auth.user && (
          <div
            id="app-nav-menu"
            role="region"
            aria-label="Menu"
            className="sm:hidden mt-3 flex flex-col gap-2 border-t border-border-divider pt-3 motion-safe:animate-fade-in"
          >
            <Link
              href="/vocabulary"
              onClick={() => setMenuOpen(false)}
              aria-current={isActive("/vocabulary") ? "page" : undefined}
              className={cn(
                navLink,
                "flex items-center min-h-11",
                isActive("/vocabulary") ? "text-accent" : "text-nav-link hover:text-em",
              )}
            >
              Vocabulary
            </Link>
            {auth.user.role === "admin" && (
              <Link
                href="/admin"
                onClick={() => setMenuOpen(false)}
                aria-current={isActive("/admin") ? "page" : undefined}
                className={cn(
                  navLink,
                  "flex items-center min-h-11",
                  isActive("/admin") ? "text-accent" : "text-nav-link hover:text-em",
                )}
              >
                Admin
              </Link>
            )}
            <div className="flex items-center justify-between gap-2 px-1 min-h-11">
              <span className="font-mono text-xs uppercase tracking-widest text-muted">Text size</span>
              <TextSizeControl />
            </div>
            <span
              className="px-1 font-mono text-xs tracking-wide text-muted truncate"
              title={auth.user.email}
            >
              {auth.user.email}
            </span>
            <button
              onClick={handleSignOut}
              className={cn(navLink, "flex items-center min-h-11 text-muted hover:text-em")}
            >
              Sign out
            </button>
          </div>
        )}
      </nav>
    </>
  );
}
